import { renderBatchEditCampaignTitle } from 'components/BatchEditCampaign/BatchEditCampaignTitle';
import { ModalState } from 'containers/Common/ModalStateFactory';
import i18n from 'i18n';
import { ModifyTrackingStateContent } from './ModifyTrackingStateContent';
import { TrackingSummaryStateContent } from './TrackingSummaryStateContent';
import { useModifyTrackingStateContentModel } from './ModifyTrackingStateContentModel';
import { useTrackingSummaryStateContentModel } from './TrackingSummaryStateContentModel';
import { L1Object, L1ObjectObjective } from 'core/l1Object/L1Object';
import { TrackingConfig } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { TrackingData } from 'core/rtbCampaign/TrackingData';

abstract class BasicTrackingManagementState {
  back?: () => void;
  next?: () => void;
  close?: (dirty: boolean, beforeClose?: () => Promise<void>) => void;

  constructor(
    protected campaigns: {
      id: number;
      name: string;
      adType: string;
    }[],
    protected trackingConfig: TrackingConfig,
    protected getContextData: () => TrackingData | undefined
  ) {}

  get renderTitle() {
    return () => renderBatchEditCampaignTitle(this.campaigns);
  }

  setBackFn = (back: () => void) => {
    this.back = back;
  };

  setNextFn = (next: () => void) => {
    this.next = next;
  };

  setCloseFn = (close: (dirty: boolean) => void) => {
    this.close = close;
  };

  canGoNext = () => {
    return true;
  };
}

export class ModifyTrackingState
  extends BasicTrackingManagementState
  implements ModalState
{
  constructor(
    private l1Object: L1Object,
    private needShowConversionTracking: boolean,
    campaigns: {
      id: number;
      name: string;
      adType: string;
    }[],
    trackingConfig: TrackingConfig,
    getContextData: () => TrackingData | undefined
  ) {
    super(campaigns, trackingConfig, getContextData);
    this.next = async () => {
      this.close && this.close(true);
    };
  }

  get content() {
    return ModifyTrackingStateContent;
  }

  get contentModel() {
    return () =>
      useModifyTrackingStateContentModel(
        this.needShowConversionTracking,
        this.trackingConfig
      );
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.goSummary'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }

  canGoNext = () => {
    const trackingData = this.getContextData();
    // objective 為 L1ObjectObjective.SALES 時，conversionTracking 必填
    if (
      this.needShowConversionTracking &&
      this.l1Object.objective === L1ObjectObjective.SALES
    ) {
      return !!trackingData && !!trackingData.conversionTracking;
    }

    // 只要使用者有填寫 conversionTracking 或 viewTrackingCodes 就可以繼續
    return (
      !!trackingData &&
      (!!trackingData.conversionTracking ||
        (!!trackingData.viewTrackingCodes &&
          trackingData.viewTrackingCodes.length > 0))
    );
  };
}

export class TrackingSummaryState
  extends BasicTrackingManagementState
  implements ModalState
{
  constructor(
    campaigns: {
      id: number;
      name: string;
      adType: string;
    }[],
    trackingConfig: TrackingConfig,
    getContextData: () => TrackingData | undefined,
    private campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    super(campaigns, trackingConfig, getContextData);
    this.next = async () => {
      const trackingData = this.getContextData();
      if (!trackingData) {
        throw new Error('Tracking data is not initialized');
      }

      this.close &&
        this.close(true, () =>
          this.campaignManager.updateTrackingData(
            this.campaigns.map((campaign) => campaign.id),
            trackingData
          )
        );
    };
  }

  get content() {
    return TrackingSummaryStateContent;
  }

  get contentModel() {
    const supportConversionTrackingType =
      !!this.trackingConfig.conversionTracking.conversionTrackingTypeOptions;
    return () =>
      useTrackingSummaryStateContentModel(supportConversionTrackingType);
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.ok'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }
}
